/** The tactical AI: what the active stack does with its turn.
 *
 * Every option comes from `enumerateLegalBattleCommands` - the same function the
 * UI highlights from and the fuzzer drives (3.3, I9) - and is scored in one
 * currency: creature `aiValue` destroyed. A blow that removes a thousand gold of
 * enemy and costs eight hundred gold of retaliation is worth two hundred, and
 * that subtraction is the whole tactical model.
 *
 * The scorer computes damage with the kernel's own `computeDamage`, fed the mean
 * of the damage range instead of a roll. That is not an estimator standing in
 * for the rules: it is the rules, evaluated at the expected value of the one
 * input that is random. Nothing here predicts how a *battle* ends - that costs a
 * real simulation, and `simulate.ts` runs one.
 */

import {
  BF_WIDTH, F_BLINDED, F_FLYING, F_NON_LIVING, F_NO_RETALIATION, F_PETRIFIED,
  F_RETALIATED, F_SHOOTER, F_WAR_MACHINE, MASTERY_FLAT_STEP, SIDE_ATTACKER, SPELL_SRC_PREFIX,
  REGEN_FLAT, REGEN_PER_POWER, VAMPIRISM_BP,
  VENGEANCE_CAP_FLAT, VENGEANCE_CAP_PER_POWER, VENGEANCE_PER_KILL_PER_POWER,
  FP_ONE, amplifyBySpecialty,
  applyDamageToPool, computeDamage, enumerateLegalBattleCommands, gateCount,
  heroCounterAvailable, heroCounterDamage, heroStamina, heroStrikeDamage, hexCol,
  hexDistance, hexNeighbours, isAlive, isWetNativeStack, magicProfile, meleeBlocked,
  racialOf, raiseCount,
  resolveStat,
  resolveTargets, ritualCost, roundHalfAway, shotCrossesWall, spellDamageAt,
  spellDurationRounds, spellManaCost, tacticsBand, heatStrokeHexes, stackHexes,
  magicStrikeFp,
} from "@game/core";
import type {
  BattleCommand, BattleState, ContentRegistry, CreatureAbility, CreatureDefId, MagicProfile,
  Modifier, SpellDef, SpellDefId, SpellEffectSpec, StackStore,
} from "@game/core";
import type { DifficultyProfile } from "./difficulty.js";
import { battleCommandKey } from "./types.js";

export interface BattleCandidate {
  readonly cmd: BattleCommand;
  readonly score: number;
  readonly why: string;
}

// ---- weights, all in units of destroyed aiValue unless noted

/** A shot takes no retaliation and cannot be walked away from. Worth a nudge. */
export const SHOOT_PREFERENCE_BP = 1500;
/** Removing a stack outright ends its damage output for the rest of the battle. */
export const WIPE_BONUS_BP = 5000;
/** Standing next to an enemy shooter silences it; worth a fraction of its value. */
export const BLOCK_SHOOTER_BP = 4000;
/** A shooter that ends its turn in melee loses its shots. */
export const SHOOTER_IN_MELEE_PENALTY_BP = 6000;
/** Closing the distance is worth something even when nothing is in range yet. */
export const APPROACH_WEIGHT = 64;
/** How much of next round's incoming fire counts, by lookahead depth. */
export const EXPOSURE_BP: readonly number[] = Object.freeze([0, 5000, 10000]);
/** The tactical AI never quits; retreat and surrender belong to the map layer. */
export const NEVER = -1_000_000_000;
/**
 * What one point of the hero's mana is worth, in destroyed aiValue.
 *
 * Only the Dark Ritual needs it, and only to answer "is giving up these
 * creatures worth this much mana". Calibrated off `MANA_PRICE_BP` below, which
 * already prices a cast's mana against the value that cast destroys: a hero's
 * whole pool tends to buy a few hundred gold of corpses per point.
 */
export const MANA_VALUE = 40;
/**
 * The share of a gated duplicate's face value the AI counts.
 *
 * A gate is worth less than the same creatures recruited: the copy dies with
 * the battle, it arrives with no modifiers, and it spends the original stack's
 * turn getting here. Half is the honest discount and it keeps the AI from
 * gating on round one when a blow was available.
 */
export const GATE_DISCOUNT_BP = 5000;
/** Rounds a war cry holds for. Mirrors `battle/activate.ts`'s own constant. */
export const WARCRY_ROUNDS = 2;
/**
 * An action that destroys something and an action that only changes position are
 * not measured in the same units, and adding them on one scale is how an AI ends
 * up walking away from a target it could have killed. A strike whose *net* value
 * is positive is lifted above every positional score; a strike that loses more
 * than it deals keeps its raw negative number and competes with moving.
 */
export const STRIKE_FLOOR = 1_000_000;
/** Positional scores are clamped below the floor so the split above holds. */
export const POSITION_CAP = 500_000;

// ------------------------------------------------------------------ helpers

function modsForStat(mods: readonly Modifier[], stat: string): Modifier[] {
  return mods.filter((m) => (m.stat as string) === stat);
}

/**
 * The mean of `rollBaseDamage` for a stack, without touching the RNG.
 *
 * `rollBaseDamage` samples the range once per creature (or ten times, scaled,
 * for a big stack). Its expectation is the midpoint times the count, which is
 * exactly what the kernel itself uses above a hundred creatures.
 */
export function expectedBaseDamage(
  s: BattleState, slot: number, countOverride?: number,
): number {
  const st = s.stacks;
  const count = countOverride ?? st.count[slot] ?? 0;
  if (count <= 0) return 0;
  const mods = st.mods[slot] ?? [];
  const lo = resolveStat(st.damageMin[slot] ?? 0, modsForStat(mods, "damageMin")).value;
  const hiRaw = resolveStat(st.damageMax[slot] ?? 0, modsForStat(mods, "damageMax")).value;
  const min = lo < 0 ? 0 : lo;
  const max = hiRaw < min ? min : hiRaw;
  return roundHalfAway(((min + max) * count) / 2);
}

export function expectedDamage(
  s: BattleState, content: ContentRegistry, attacker: number, defender: number,
  ranged: boolean, hexesTravelled = 0, countOverride?: number, magic = false,
): number {
  const base = expectedBaseDamage(s, attacker, countOverride);
  if (base <= 0) return 0;
  const st = s.stacks;
  const wallObstructed = ranged
    && shotCrossesWall(s, st.hex[attacker] ?? 0, st.hex[defender] ?? 0);
  // `magic` is the difference between the two blows a charged creature can
  // throw, and without it here they scored identically - so the AI picked
  // between them arbitrarily and spent both of a Thunder Colossus's charges on a Battle
  // Dwarf, the one target where the charge is WORSE than a plain swing.
  // Measured before this line existed: charged=2 on the Dwarf, charged=0 on
  // the Spearwarden. An enumerated command the scorer cannot tell apart is worse
  // than one it never sees.
  const ab = magic ? magicAttackOf(content, st.def[attacker]!) : null;
  return computeDamage(s, content, {
    attacker, defender, baseDamage: base, ranged, luck: 0,
    hexesTravelled, wallObstructed, retaliation: false,
    ...(ab === null ? {} : { magicFp: magicStrikeFp(s, content, attacker, defender, ab) }),
  }).value;
}

/** The charged-blow ability a creature carries, or null. */
function magicAttackOf(
  content: ContentRegistry, def: CreatureDefId,
): Extract<CreatureAbility, { kind: "MAGIC_ATTACK" }> | null {
  for (const a of content.creatures.get(def)?.abilities ?? []) {
    if (a.kind === "MAGIC_ATTACK") return a;
  }
  return null;
}

/** Damage converted into destroyed value, with a bonus for emptying the slot. */
export function damageValue(
  s: BattleState, content: ContentRegistry, target: number, damage: number,
): number {
  const st = s.stacks;
  const def = content.creatures.get(st.def[target]!);
  const aiValue = def?.aiValue ?? 0;
  if (aiValue <= 0 || damage <= 0) return 0;
  const count = st.count[target] ?? 0;
  const maxHp = st.maxHp[target] ?? 1;
  const cas = applyDamageToPool(count, st.topHp[target] ?? maxHp, maxHp, damage);
  let v = Math.floor((aiValue * cas.damage) / (maxHp > 0 ? maxHp : 1));
  if (cas.killed >= count && count > 0) {
    v += Math.floor((aiValue * count * WIPE_BONUS_BP) / 10000);
  }
  return v;
}

function creatureHas(
  st: StackStore, content: ContentRegistry, slot: number, kind: string,
): boolean {
  const def = content.creatures.get(st.def[slot]!);
  if (def === undefined) return false;
  for (const a of def.abilities) if (a.kind === kind) return true;
  return false;
}

/** Will `target` hit back if `attacker` strikes it in melee? */
export function retaliates(
  s: BattleState, content: ContentRegistry, attacker: number, target: number,
): boolean {
  const st = s.stacks;
  if (((st.flags[attacker] ?? 0) & F_NO_RETALIATION) !== 0) return false;
  if (((st.flags[target] ?? 0) & (F_BLINDED | F_PETRIFIED)) !== 0) return false;
  if (((st.flags[target] ?? 0) & F_RETALIATED) === 0) return true;
  return creatureHas(st, content, target, "UNLIMITED_RETALIATION")
    || creatureHas(st, content, target, "EXTRA_RETALIATIONS");
}

const isShooter = (st: StackStore, i: number): boolean =>
  ((st.flags[i] ?? 0) & F_SHOOTER) !== 0;

function enemiesAdjacentTo(s: BattleState, side: number, hex: number): number[] {
  const out: number[] = [];
  const nb: number[] = [];
  const n = hexNeighbours(hex, nb);
  const st = s.stacks;
  for (let k = 0; k < n; k++) {
    for (let i = 0; i < st.n; i++) {
      if (!isAlive(st, i) || st.side[i] === side) continue;
      if (st.hex[i] === nb[k]) out.push(i);
    }
  }
  return out;
}

/**
 * Value of the fire this stack invites by standing on `hex` when its turn ends.
 * A heuristic weight, not a prediction: it says "that square is dangerous", and
 * the only thing it can do is push the score of one legal command below another.
 *
 * The per-enemy damage does not depend on the hex, only on who is in range, so
 * it is computed once per enemy and cached: a stack with a hundred and sixty
 * legal destinations would otherwise re-run the damage formula a thousand times
 * to answer the same question.
 */
function exposureAt(
  s: BattleState, content: ContentRegistry, slot: number, hex: number,
  cache: Map<number, number>,
): number {
  const st = s.stacks;
  const side = st.side[slot] ?? 0;
  let v = 0;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] === side) continue;
    const shooter = isShooter(st, i);
    const reach = shooter
      ? BF_WIDTH * 2
      : (st.speed[i] ?? 0) + (((st.flags[i] ?? 0) & F_FLYING) !== 0 ? 2 : 1);
    if (hexDistance(st.hex[i] ?? 0, hex) > reach) continue;
    let hit = cache.get(i);
    if (hit === undefined) {
      hit = incomingValue(s, content, i, slot, shooter);
      cache.set(i, hit);
    }
    v += hit;
  }
  return v;
}

/** Damage landing on one of our own stacks, priced in the same currency. */
function valueLost(
  s: BattleState, content: ContentRegistry, self: number, damage: number,
): number {
  return damageValue(s, content, self, damage);
}

/** Expected value we lose if `attacker` hits `self` with a full-strength blow. */
function incomingValue(
  s: BattleState, content: ContentRegistry, attacker: number, self: number, ranged: boolean,
): number {
  return valueLost(s, content, self, expectedDamage(s, content, attacker, self, ranged));
}

/** The most valuable enemy stack, for "walk toward something worth hitting". */
function bestEnemy(
  s: BattleState, content: ContentRegistry, side: number,
): { slot: number; value: number } {
  const st = s.stacks;
  let slot = -1, value = 0;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] === side) continue;
    const def = content.creatures.get(st.def[i]!);
    const v = (def?.aiValue ?? 0) * (st.count[i] ?? 0) * (isShooter(st, i) ? 2 : 1);
    if (v > value) { value = v; slot = i; }
  }
  return { slot, value };
}

// -------------------------------------------------------------------- score

function scoreAttack(
  s: BattleState, content: ContentRegistry, p: DifficultyProfile,
  slot: number, fromHex: number, target: number, cache: Map<number, number>,
  magic = false,
): number {
  const st = s.stacks;
  const here = st.hex[slot] ?? 0;
  const travelled = hexDistance(here, fromHex);
  const dealt = expectedDamage(s, content, slot, target, false, travelled, undefined, magic);
  let score = damageValue(s, content, target, dealt);
  // A charge is a finite resource, so an identical blow that spends one is
  // worth strictly less than the one that does not. Without this the two
  // score equal against a target that resists nothing and the tie breaks
  // arbitrarily - burning the budget on whatever came first.
  if (magic) score -= Math.max(1, Math.floor(score / 10));

  // Retaliation is struck by whatever survives the blow, which is why the
  // casualties are applied first and the reply is rolled at the reduced count.
  if (retaliates(s, content, slot, target)) {
    const maxHp = st.maxHp[target] ?? 1;
    const cas = applyDamageToPool(st.count[target] ?? 0, st.topHp[target] ?? maxHp, maxHp, dealt);
    const left = (st.count[target] ?? 0) - cas.killed;
    if (left > 0) {
      const back = expectedDamage(s, content, target, slot, false, 0, left);
      score -= valueLost(s, content, slot, back);
    }
  }

  const exposureBp = EXPOSURE_BP[p.battleLookahead] ?? 0;
  if (exposureBp > 0) {
    score -= Math.floor((exposureAt(s, content, slot, fromHex, cache) * exposureBp) / 10000);
  }

  // A shooter that *walks* into melee is a worse shooter next round. One that is
  // already stuck there has nothing to lose by swinging.
  if (isShooter(st, slot) && fromHex !== here) {
    const def = content.creatures.get(st.def[slot]!);
    const own = (def?.aiValue ?? 0) * (st.count[slot] ?? 0);
    score -= Math.floor((own * SHOOTER_IN_MELEE_PENALTY_BP) / 10000);
  }
  return score;
}

function scoreShoot(
  s: BattleState, content: ContentRegistry, slot: number, target: number,
): number {
  const dealt = expectedDamage(s, content, slot, target, true);
  const v = damageValue(s, content, target, dealt);
  return v + Math.floor((v * SHOOT_PREFERENCE_BP) / 10000);
}

function scoreMove(
  s: BattleState, content: ContentRegistry, p: DifficultyProfile,
  slot: number, hex: number, cache: Map<number, number>,
): number {
  const st = s.stacks;
  const side = st.side[slot] ?? 0;
  let score = 0;

  const adjacent = enemiesAdjacentTo(s, side, hex);
  if (isShooter(st, slot)) {
    // Keep shooters back: distance from the nearest melee threat is the whole
    // positional value, and touching anything is a disaster.
    let nearest = BF_WIDTH * 2;
    for (let i = 0; i < st.n; i++) {
      if (!isAlive(st, i) || st.side[i] === side || isShooter(st, i)) continue;
      const d = hexDistance(st.hex[i] ?? 0, hex);
      if (d < nearest) nearest = d;
    }
    score += nearest * APPROACH_WEIGHT;
    const def = content.creatures.get(st.def[slot]!);
    const own = (def?.aiValue ?? 0) * (st.count[slot] ?? 0);
    if (adjacent.length > 0) {
      score -= Math.floor((own * SHOOTER_IN_MELEE_PENALTY_BP) / 10000);
    }
  } else {
    // Block enemy shooters with whatever can reach them.
    for (const e of adjacent) {
      if (!isShooter(st, e)) continue;
      const def = content.creatures.get(st.def[e]!);
      const v = (def?.aiValue ?? 0) * (st.count[e] ?? 0);
      score += Math.floor((v * BLOCK_SHOOTER_BP) / 10000);
    }
    const target = bestEnemy(s, content, side);
    if (target.slot >= 0) {
      const d = hexDistance(st.hex[target.slot] ?? 0, hex);
      // Linear in the distance - the shooter branch above with the sign
      // flipped - and weighted by what the target is worth.
      //
      // This was `APPROACH_WEIGHT * value / ((d + 1) * 1000)`, and the integer
      // division flattened it: measured on a real game, every reachable hex on
      // the board scored the same 17, so a melee stack had no directional pull
      // whatever and took whichever equal-scoring hex the enumerator listed
      // first. Two such stacks then orbited each other forever - seed 4242,
      // 36x36, two AI players: a battle still ongoing at round 100 with the
      // counts unchanged since round 39 and not one BATTLE_ATTACK enumerated
      // in between, because neither side ever closed to contact.
      const pull = 1 + Math.floor(target.value / 1000);
      score += (BF_WIDTH * 2 - d) * APPROACH_WEIGHT * pull;
    }
  }

  const exposureBp = EXPOSURE_BP[p.battleLookahead] ?? 0;
  if (exposureBp > 0) {
    score -= Math.floor((exposureAt(s, content, slot, hex, cache) * exposureBp) / 10000);
  }
  return score < -POSITION_CAP ? -POSITION_CAP : score > POSITION_CAP ? POSITION_CAP : score;
}

/**
 * Tactics placement. Only a move that strictly improves a stack's column toward
 * its ideal is scored above zero, so the phase cannot loop: every accepted
 * command reduces a non-negative integer, and when none is left `DONE` wins.
 */
function scoreTactics(
  s: BattleState, content: ContentRegistry, side: 0 | 1, slot: number, hex: number,
): number {
  const st = s.stacks;
  const band = tacticsBand(s);
  const ideal = isShooter(st, slot)
    ? (side === SIDE_ATTACKER ? 0 : BF_WIDTH - 1)
    : (side === SIDE_ATTACKER ? band - 1 : BF_WIDTH - band);
  const now = Math.abs(hexCol(st.hex[slot] ?? 0) - ideal);
  const next = Math.abs(hexCol(hex) - ideal);
  if (next >= now) return NEVER;
  void content;
  return (now - next) * 100;
}

// ------------------------------------------------------------------- magic
//
// A spell is priced in exactly the currency every other action is priced in:
// destroyed `aiValue`. That is what lets `scoreBattleCommands` put a Fireball
// and a charge on one list and take the maximum, which is the right comparison
// because a hero's cast spends the active stack's action - the two options are
// the same turn spent two ways, not one on top of the other.
//
// Damage is read out of the kernel (`spellDamageAt`, the same function
// `execute.ts` calls), so mastery, Spellcraft, elemental protection and
// vulnerability are all already in the number. Buffs and debuffs cannot be read
// off a table because what a `+2 attack` is worth depends entirely on who is
// holding it, so they are *measured*: the spell's modifiers are laid on a
// throwaway copy of the battle state and the kernel's own damage formula is run
// again. Nothing here knows the name of a single spell.

/** Rounds of a buff or debuff the scorer will bank on. Beyond this the board
 *  has changed enough that the measurement is fiction. */
export const SPELL_HORIZON_ROUNDS = 3;
/**
 * Mana is a budget for the whole battle, not for the round in front of us.
 *
 * One cast may spend at most this share of what is *left*, which makes the pool
 * decay geometrically and can therefore never be emptied: a hero with 100 mana
 * casts at most 34 in round one, at most 22 of the remaining 66 in round two,
 * and still has something in round ten. That is the whole model - no schedule,
 * no round counter, and it stays correct when a battle runs long.
 */
export const MANA_SHARE_BP = 3400;
/** ...with a floor, or a nearly-empty pool freezes below its own cheapest spell. */
export const MANA_FLOOR = 6;
/** Value docked in proportion to the share of the pool a cast burns. */
export const MANA_PRICE_BP = 6000;
/** What a point of damage on one's own stack costs, against one on theirs. */
export const SELF_HARM_BP = 15000;

/**
 * What the `ADVENTURE` arm of a spell effect is worth, as a change to the
 * target's own output and to what lands on it, in basis points.
 *
 * These are weights, not rules: the rule itself lives in the kernel, and the
 * kernel's rules for these are flags and fixed-point tables the scorer cannot
 * lay on a state copy the way it can a `Modifier`. Keyed by the rule id the
 * content author wrote, so this is a table over content, not a switch on a
 * spell - `spell.blind` and any future spell that blinds are priced together.
 */
export const RULE_VALUE_BP: Readonly<Record<string, readonly [number, number]>> =
  Object.freeze({
    // [ delta to the target's damage output, delta to the damage it takes ]
    "rule.blind": [-10000, 0],
    "rule.damage_roll_max": [1800, 0],
    "rule.damage_roll_min": [-1800, 0],
    "rule.frenzy": [2500, 4000],
    "rule.befuddle": [-2500, 0],
    "rule.melee_damage_reduction": [0, -1200],
    // Enchanted weapons: +15% to the blows the target throws, which is exactly
    // what `ENCHANT_WEAPON_PCT` installs. Priced as output rather than as
    // protection, because that is the side of the exchange it lands on.
    //
    // Absent, they scored zero and fell through to NEVER - so an AI Mage held
    // its casts for Slow and Curse and enchanted nothing, ever. Measured
    // before this entry existed: flame/frost/storm/stone all at -1e9 against
    // Curse at 1001287. A rule this table does not name is not merely
    // unvalued, it is refused.
    "rule.weapon.air": [1500, 0],
    "rule.weapon.earth": [1500, 0],
    "rule.weapon.fire": [1500, 0],
    "rule.weapon.water": [1500, 0],
    "rule.ranged_damage_reduction": [0, -1500],
    // Elemental wards and Null Ward cut *spell* damage, which the physical
    // exchange below cannot see. They are worth a token amount so that casting
    // one is never free, and never worth more than a blow.
    "rule.protection.air": [0, -500],
    "rule.protection.earth": [0, -500],
    "rule.protection.fire": [0, -500],
    "rule.protection.water": [0, -500],
    "rule.null_ward": [0, -400],
  });
/** Earthquake damages the works rather than a stack; priced like the catapult. */
export const EARTHQUAKE_VALUE = 200;

/**
 * Hornet Cloud's delay, as a share of one round of the target's own output.
 *
 * Half, and NOT multiplied by the spell's duration: the stack loses its place
 * in one round and gets a full turn in the next, so a horizon multiplier would
 * price a tempo swing as a three-round debuff. Half rather than all of it
 * because a delayed stack still acts - it acts last, which is worth something
 * to it and costs it the initiative.
 */
export const DELAY_TEMPO_BP = 5000;

/**
 * The best exchange each stack is part of, measured once per decision.
 *
 * `out[i]` is the most value `i` can destroy in one blow and `victim[i]` is who
 * it destroys it from; `inn[i]` is the most value an enemy of `i` can destroy
 * by hitting `i`, and `threat[i]` is who does it. Both are the same n-squared
 * sweep of the damage formula, so they are collected together, and a re-measure
 * after a modifier lands only has to re-run the two pairings rather than the
 * sweep.
 */
export interface Exchange {
  readonly out: readonly number[];
  readonly inn: readonly number[];
  readonly victim: readonly number[];
  readonly threat: readonly number[];
  readonly ranged: readonly boolean[];
}

function shootsNow(s: BattleState, slot: number): boolean {
  const st = s.stacks;
  return isShooter(st, slot) && (st.shots[slot] ?? 0) > 0 && !meleeBlocked(s, slot);
}

export function measureExchange(s: BattleState, content: ContentRegistry): Exchange {
  const st = s.stacks;
  const n = st.n;
  const out = new Array<number>(n).fill(0);
  const inn = new Array<number>(n).fill(0);
  const victim = new Array<number>(n).fill(-1);
  const threat = new Array<number>(n).fill(-1);
  const ranged = new Array<boolean>(n).fill(false);
  for (let i = 0; i < n; i++) ranged[i] = isAlive(st, i) && shootsNow(s, i);

  for (let i = 0; i < n; i++) {
    if (!isAlive(st, i)) continue;
    for (let j = 0; j < n; j++) {
      if (!isAlive(st, j) || st.side[j] === st.side[i]) continue;
      const v = damageValue(s, content, j, expectedDamage(s, content, i, j, ranged[i] === true));
      if (victim[i] === -1 || v > (out[i] ?? 0)) { out[i] = v; victim[i] = j; }
      if (threat[j] === -1 || v > (inn[j] ?? 0)) { inn[j] = v; threat[j] = i; }
    }
  }
  return { out, inn, victim, threat, ranged };
}

/** The same state with extra modifiers on one stack. Nothing is mutated: only
 *  the `mods` row is copied, so this costs one small array per measurement. */
function withMods(s: BattleState, slot: number, extra: readonly Modifier[]): BattleState {
  const mods = [...s.stacks.mods];
  mods[slot] = [...(mods[slot] ?? []), ...extra];
  return { ...s, stacks: { ...s.stacks, mods } };
}

/** The same state with every spell-sourced modifier stripped off one stack -
 *  exactly the scope the kernel's `DISPEL` strips. */
function withoutSpellMods(s: BattleState, slot: number): BattleState {
  const cur = s.stacks.mods[slot] ?? [];
  const keep = cur.filter((m) => !(m.source as string).startsWith(SPELL_SRC_PREFIX));
  if (keep.length === cur.length) return s;
  const mods = [...s.stacks.mods];
  mods[slot] = keep;
  return { ...s, stacks: { ...s.stacks, mods } };
}

function outgoingIn(
  s: BattleState, content: ContentRegistry, x: Exchange, slot: number,
): number {
  const j = x.victim[slot] ?? -1;
  if (j < 0 || !isAlive(s.stacks, j)) return 0;
  return damageValue(s, content, j, expectedDamage(s, content, slot, j, x.ranged[slot] === true));
}

function incomingIn(
  s: BattleState, content: ContentRegistry, x: Exchange, slot: number,
): number {
  const i = x.threat[slot] ?? -1;
  if (i < 0 || !isAlive(s.stacks, i)) return 0;
  return damageValue(s, content, slot, expectedDamage(s, content, i, slot, x.ranged[i] === true));
}

/**
 * What changing one stack's stats is worth to the caster, over `rounds`.
 *
 * The sign is the whole trick and it is symmetric: on one of ours, dealing more
 * and taking less is a gain; on one of theirs, dealing less and taking more is
 * the same gain. Buffs and debuffs are therefore one code path, and "the damage
 * a buff adds" and "the damage a debuff denies" are literally the same
 * subtraction run on opposite sides.
 */
function statChangeValue(
  s: BattleState, content: ContentRegistry, x: Exchange, slot: number,
  friendly: boolean, after: BattleState, rounds: number,
): number {
  const dOut = outgoingIn(after, content, x, slot) - (x.out[slot] ?? 0)
    + tempoDelta(s, after, x, slot);
  const dIn = incomingIn(after, content, x, slot) - (x.inn[slot] ?? 0);
  const perRound = friendly ? dOut - dIn : dIn - dOut;
  return perRound * (rounds < SPELL_HORIZON_ROUNDS ? rounds : SPELL_HORIZON_ROUNDS);
}

/**
 * The half of a buff the damage formula cannot see.
 *
 * Haste and Slow move no number the formula reads, and a scorer built only on
 * `computeDamage` prices them both at exactly zero - which is how an AI ends up
 * never casting the two most-cast spells in the game. Speed, morale and luck all
 * buy the same thing, *more blows*, so all three are converted into a fraction
 * of the blow this stack already lands. Read off the stat keys the modifier
 * carries, so any spell or artifact that moves them is priced the same way.
 */
export const SPEED_TEMPO_BP = 5000;
export const MORALE_TEMPO_BP = 800;
export const LUCK_TEMPO_BP = 800;

function statOf(s: BattleState, slot: number, key: string, base: number): number {
  return resolveStat(base, modsForStat(s.stacks.mods[slot] ?? [], key)).value;
}

function tempoDelta(
  before: BattleState, after: BattleState, x: Exchange, slot: number,
): number {
  const out = x.out[slot] ?? 0;
  if (out === 0) return 0;
  const st = before.stacks;
  const sp0 = statOf(before, slot, "speed", st.speed[slot] ?? 0);
  const sp1 = statOf(after, slot, "speed", st.speed[slot] ?? 0);
  const mo = statOf(after, slot, "morale", st.morale[slot] ?? 0)
    - statOf(before, slot, "morale", st.morale[slot] ?? 0);
  const lu = statOf(after, slot, "luck", st.luck[slot] ?? 0)
    - statOf(before, slot, "luck", st.luck[slot] ?? 0);
  let bp = mo * MORALE_TEMPO_BP + lu * LUCK_TEMPO_BP;
  if (sp1 !== sp0) {
    const base = sp0 > 0 ? sp0 : 1;
    bp += Math.floor(((sp1 - sp0) * SPEED_TEMPO_BP) / base);
  }
  return Math.floor((out * bp) / 10000);
}

/** A rule effect, priced off the same two numbers by the weight table above. */
function ruleValue(
  x: Exchange, rule: string, slot: number, friendly: boolean, rounds: number,
): number {
  const w = RULE_VALUE_BP[rule];
  if (w === undefined) return 0;
  const dOut = Math.floor(((x.out[slot] ?? 0) * w[0]) / 10000);
  const dIn = Math.floor(((x.inn[slot] ?? 0) * w[1]) / 10000);
  const perRound = friendly ? dOut - dIn : dIn - dOut;
  return perRound * (rounds < SPELL_HORIZON_ROUNDS ? rounds : SPELL_HORIZON_ROUNDS);
}

/** `spellRestoreAt`'s arithmetic: the kernel keeps it private, the tables are public. */
function restoreAmount(e: SpellEffectSpec, prof: MagicProfile): number {
  const flat = e.flat ?? 0;
  const step = MASTERY_FLAT_STEP[prof.mastery] ?? 0;
  return (e.perPower ?? 0) * prof.spellPower + flat + (flat > 0 ? flat * step : 0);
}

function abilitiesOfSlot(
  s: BattleState, content: ContentRegistry, slot: number,
): readonly CreatureAbility[] {
  return content.creatures.get(s.stacks.def[slot]!)?.abilities ?? [];
}

/** Basis points of a cast that survive the target's MAGIC_RESISTANCE roll. */
function throughResistance(
  s: BattleState, content: ContentRegistry, slot: number, casterSide: number,
): number {
  if (s.stacks.side[slot] === casterSide) return 10000;
  let pct = 0;
  for (const a of abilitiesOfSlot(s, content, slot)) {
    if (a.kind === "MAGIC_RESISTANCE") pct += a.pct;
  }
  if (pct <= 0) return 10000;
  return 10000 - (pct > 100 ? 100 : pct) * 100;
}

function effectValue(
  s: BattleState, content: ContentRegistry, x: Exchange, def: SpellDef,
  e: SpellEffectSpec, prof: MagicProfile, slot: number, friendly: boolean,
): number {
  const st = s.stacks;
  switch (e.kind) {
    case "DAMAGE": {
      const v = damageValue(s, content, slot, spellDamageAt(s, content, def, e, prof, slot));
      // Worldfire and Grave Wave hit their own side too, and a straight
      // subtraction says a mutual wipe is fine as long as theirs is bigger. It
      // is not: the enemy loss is replaced by the next town they own, ours has
      // to be re-bought, and a battle both armies leave empty is a battle both
      // sides lost. Self-inflicted casualties are therefore priced above enemy
      // ones, which is what stops the AI trading its army for a draw.
      return friendly ? -Math.floor((v * SELF_HARM_BP) / 10000) : v;
    }
    case "HEAL": case "RESURRECT": {
      const maxHp = st.maxHp[slot] ?? 1;
      const missing = e.kind === "HEAL"
        ? maxHp - (st.topHp[slot] ?? maxHp)
        : restoreAmount(e, prof);
      const healed = Math.min(restoreAmount(e, prof), missing < 0 ? 0 : missing);
      const aiValue = content.creatures.get(st.def[slot]!)?.aiValue ?? 0;
      const v = Math.floor((aiValue * healed) / (maxHp > 0 ? maxHp : 1));
      return friendly ? v : -v;
    }
    case "MODIFIER": {
      const list = e.modifiers ?? [];
      if (list.length === 0) return 0;
      const rounds = spellDurationRounds(e, prof);
      // Scored through the caster's specialty, because that is what will land:
      // Lorauton's Haste is a bigger swing than anyone else's and the scorer that
      // priced the authored +3 under-bid his own signature spell.
      const cast = list.map((m) => amplifyBySpecialty(m, prof.specialtyFp));
      return statChangeValue(s, content, x, slot, friendly, withMods(s, slot, cast), rounds);
    }
    case "DISPEL":
      // The same measurement run against the stack with its magic taken off.
      // The sign falls out of it: stripping our own Bless costs us the damage
      // it was adding, stripping their Haste denies them theirs, and there is
      // no polarity flag in the effect for the scorer to read instead.
      return statChangeValue(
        s, content, x, slot, friendly, withoutSpellMods(s, slot), SPELL_HORIZON_ROUNDS,
      );
    case "ADVENTURE": {
      const rule = e.adventure ?? "";
      if (rule === "siege.earthquake") return EARTHQUAKE_VALUE;
      const rounds = spellDurationRounds(e, prof);
      const horizon = rounds < SPELL_HORIZON_ROUNDS ? rounds : SPELL_HORIZON_ROUNDS;
      /**
       * Four rules that `RULE_VALUE_BP` cannot express, because none of them is
       * a per-round change to what a stack deals and takes. They are priced
       * with the kernel's own constants rather than with a second copy - see
       * the note on the exports in `battle/index.ts`.
       */
      switch (rule) {
        case "rule.delay": {
          // A tempo swing, not a debuff: one round of initiative, once.
          const v = Math.floor(((x.out[slot] ?? 0) * DELAY_TEMPO_BP) / 10000);
          return friendly ? -Math.floor((v * SELF_HARM_BP) / 10000) : v;
        }
        case "rule.regeneration": {
          /**
           * Priced as the HEAL it is - and capped at the damage it could
           * actually prevent.
           *
           * Hit points restored are not worth hit points dealt: a heal only
           * pays when the blow it undoes arrives. `Exchange.inn[slot]` is the
           * most an enemy can destroy by hitting this stack, in the same
           * aiValue currency, so it is the honest ceiling. Without it the
           * scorer valued a full three rounds of mending on a stack nobody was
           * shooting at, and it out-bid Arcane Dart on a healthy front rank.
           */
          const maxHp = st.maxHp[slot] ?? 1;
          const perTurn = REGEN_FLAT + REGEN_PER_POWER * prof.spellPower;
          const aiValue = content.creatures.get(st.def[slot]!)?.aiValue ?? 0;
          const mended = Math.floor((aiValue * perTurn * horizon) / (maxHp > 0 ? maxHp : 1));
          const threatened = (x.inn[slot] ?? 0) * horizon;
          const v = mended < threatened ? mended : threatened;
          return friendly ? v : -v;
        }
        case "rule.life_drain": {
          // What the stack heals is a share of what it deals, so its own output
          // is the measure - which is exactly what `Exchange.out` holds.
          const bp = VAMPIRISM_BP[prof.mastery] ?? 3000;
          const v = Math.floor(((x.out[slot] ?? 0) * bp * horizon) / 10000);
          return friendly ? v : -v;
        }
        case "rule.holy_retribution": {
          // The bill the kernel will actually present, computed here the same
          // way and capped the same way. Zero against a stack that has killed
          // nothing, which is the point of the spell and has to be the point of
          // its score too, or the AI would open with it every battle.
          const owed = st.killed[slot] ?? 0;
          if (owed <= 0) return 0;
          const raw = owed * VENGEANCE_PER_KILL_PER_POWER * prof.spellPower;
          const cap = VENGEANCE_CAP_FLAT + VENGEANCE_CAP_PER_POWER * prof.spellPower;
          const v = damageValue(s, content, slot, raw > cap ? cap : raw);
          return friendly ? -Math.floor((v * SELF_HARM_BP) / 10000) : v;
        }
        default: break;
      }
      return ruleValue(x, rule, slot, friendly, rounds);
    }
    default:
      return 0;
  }
}

/** Every effect this cast will actually run, expert bonus first, as the kernel does. */
function effectsOf(def: SpellDef, prof: MagicProfile): readonly SpellEffectSpec[] {
  const out: SpellEffectSpec[] = [];
  if (prof.mastery >= 3 && def.expertBonus !== undefined) out.push(def.expertBonus);
  for (const e of def.effects) out.push(e);
  return out;
}

/**
 * What one cast is worth, before its mana is priced. The targets come from the
 * kernel's own `resolveTargets`, so a mass spell is scored over exactly the
 * stacks it will land on - including our own, when the spell does not care.
 */
export function castValue(
  s: BattleState, content: ContentRegistry, x: Exchange, casterSide: 0 | 1,
  spell: SpellDefId, target: number, prof: MagicProfile,
): number {
  const def = content.spells.get(spell);
  if (def === undefined) return 0;
  const targets = resolveTargets(s, content, def, casterSide, target);
  if (!targets.ok) return 0;
  const effects = effectsOf(def, prof);
  let total = 0;
  for (const slot of targets.value.stacks) {
    if (!isAlive(s.stacks, slot)) continue;
    const friendly = s.stacks.side[slot] === casterSide;
    let v = 0;
    for (const e of effects) v += effectValue(s, content, x, def, e, prof, slot, friendly);
    if (v > 0) v = Math.floor((v * throughResistance(s, content, slot, casterSide)) / 10000);
    total += v;
  }
  return total;
}

/** The most mana one cast may spend out of what the hero has left. */
export function manaAllowance(mana: number): number {
  const share = Math.floor((mana * MANA_SHARE_BP) / 10000);
  return share > MANA_FLOOR ? share : MANA_FLOOR;
}

/** A hero's cast: worth what it does, less what it costs the rest of the battle. */
function scoreCast(
  s: BattleState, content: ContentRegistry, x: Exchange, side: 0 | 1,
  spell: SpellDefId, target: number,
): number {
  const def = content.spells.get(spell);
  if (def === undefined) return NEVER;
  const prof = magicProfile(s, side, def);
  const mana = s.heroes[side].mana;
  const cost = spellManaCost(def, prof);
  // The budget is a hard gate rather than a weight: a spell the hero cannot
  // afford to repeat is one it should not be casting yet, however good it looks
  // on this board. `NEVER` keeps it below `BATTLE_DEFEND`, which always exists.
  if (cost > manaAllowance(mana)) return NEVER;

  const value = castValue(s, content, x, side, spell, target, prof);
  if (value <= 0) return value;
  const burnBp = mana <= 0 ? 10000 : Math.min(10000, Math.floor((cost * 10000) / mana));
  return value - Math.floor((value * burnBp * MANA_PRICE_BP) / 100_000_000);
}

/** A creature's cast: no mana and no mastery, and the ability's own power. */
function scoreCreatureCast(
  s: BattleState, content: ContentRegistry, x: Exchange, slot: number,
  spell: SpellDefId, target: number,
): number {
  let power = -1;
  for (const a of abilitiesOfSlot(s, content, slot)) {
    if (a.kind === "CASTER" && a.spells.includes(spell)) power = a.power;
  }
  if (power < 0) return 0;
  const side = (s.stacks.side[slot] === 1 ? 1 : 0) as 0 | 1;
  const prof: MagicProfile =
    { carried: false, spellPower: power, mastery: 0, wisdom: 3, specialtyFp: FP_ONE, mods: [] };
  return castValue(s, content, x, side, spell, target, prof);
}

/**
 * How much of the army the hero is putting at risk with this blow, or null when
 * the blow would knock him out and end the battle.
 *
 * The shape is deliberately non-linear. A hero at full stamina is risking one
 * counterblow and little else; a hero one blow from the floor is risking the
 * entire army, because the knockout is a retreat and a retreat loses everything
 * he is carrying. Charging the linear share would have him keep swinging until
 * the round he dies.
 *
 * Priced as a fraction of what is actually at stake - the side's own army in
 * the same `aiValue` currency the whole file trades in - so it scales with the
 * battle rather than being a constant that is enormous in a skirmish and
 * nothing in a siege.
 */
export const HERO_RISK_FLOOR = 6000;

export function heroCounterCost(
  s: BattleState, content: ContentRegistry, side: 0 | 1, target: number, dealt: number,
): number | null {
  // A stack the blow kills cannot answer, so there is nothing to charge.
  const pool = (s.stacks.count[target]! - 1) * s.stacks.maxHp[target]! + s.stacks.topHp[target]!;
  if (dealt >= pool) return 0;
  if (!heroCounterAvailable(s, target)) return 0;

  const taken = heroCounterDamage(s, target);
  const room = heroStamina(s, side) - s.heroes[side].wounds;
  let ours = 0;
  for (let i = 0; i < s.stacks.n; i++) {
    if (!isAlive(s.stacks, i) || s.stacks.side[i] !== side) continue;
    ours += (content.creatures.get(s.stacks.def[i]!)?.aiValue ?? 0) * s.stacks.count[i]!;
  }
  // The blow that ends him. Never worth it: the retreat writes off `ours`
  // entirely, and no single stack on the board is worth an army.
  if (taken >= room) return null;
  // Otherwise: the share of the army this blow risks, which is the share of the
  // remaining room it eats, floored so that even a comfortable hero pays
  // something for leaning in.
  const share = Math.floor((taken * HERO_RISK_FLOOR) / (room > 0 ? room : 1));
  return Math.floor((ours * share) / 10000);
}

/**
 * What spending the one racial charge is worth on this turn.
 *
 * Deliberately below `STRIKE_FLOOR`, which every killing blow is lifted above:
 * a racial that outbid an attack would be spent in round one of every battle,
 * and two of the three are worth more later. The `rage` arm is the clearest
 * case - it pays per fallen stack, so the AI should be reaching for it when the
 * line is breaking and not when it is forming up.
 */
function scoreRacial(
  s: BattleState, content: ContentRegistry, x: Exchange, side: 0 | 1, slot: number,
): number {
  const racial = racialOf(s, content, side);
  if (racial === null) return NEVER;
  const st = s.stacks;
  switch (racial.kind) {
    case "gate": {
      const def = content.creatures.get(st.def[slot]!);
      const count = gateCount(st.count[slot]!, racial.pctBp);
      const face = (def?.aiValue ?? 0) * count;
      return Math.floor((face * GATE_DISCOUNT_BP) / 10000);
    }
    case "rage": {
      // Attack points across the living line, priced at what one point of
      // attack is worth on the board: the damage bracket moves 5% per point,
      // so the army-wide gain is a share of what the army can still deal.
      let fallen = 0;
      let living = 0;
      for (let i = 0; i < st.n; i++) {
        if (st.side[i] !== side) continue;
        if (isAlive(st, i)) living += 1; else fallen += 1;
      }
      const raw = racial.attack + racial.perFallen * fallen;
      const gain = raw > racial.cap ? racial.cap : raw;
      return gain * living * 8;
    }
    case "ritual": {
      const def = content.creatures.get(st.def[slot]!);
      const given = ritualCost(st.count[slot]!, racial.costPctBp);
      if (given <= 0) return NEVER;
      const per = racial.hpPerMana < 1 ? 1 : racial.hpPerMana;
      const mana = Math.ceil((given * st.maxHp[slot]!) / per);
      // Mana the hero cannot hold is mana the ritual did not buy.
      return mana * MANA_VALUE - given * (def?.aiValue ?? 0);
    }
    /**
     * The promotion, priced at what the army gains: the difference in aiValue
     * between what the stack is and what it becomes, over its whole count. That
     * is the one honest number here - a Spearwarden stack of eighty becoming
     * Glaivewardens is worth eighty times the gap, and a stack of three is not.
     */
    case "train": {
      const now = content.creatures.get(st.def[slot]!)?.aiValue ?? 0;
      let up = 0;
      for (const cd of content.creatures.values()) {
        if (cd.upgradeOf === st.def[slot]) { up = cd.aiValue; break; }
      }
      const gain = up - now;
      return gain <= 0 ? NEVER : Math.floor((gain * st.count[slot]!) / 2);
    }
    case "swiftness": {
      let living = 0;
      for (let i = 0; i < st.n; i++) if (isAlive(st, i) && st.side[i] === side) living += 1;
      // Speed is tempo rather than damage, so it is priced below an attack
      // point: it buys reach and initiative, not a bigger blow.
      return racial.speed * living * 5;
    }
    /**
     * A second cast this round, which is worth the best cast the hero could
     * make - and that is exactly what the scorer has already computed for every
     * other candidate in this list. Rather than re-derive it, the mana is
     * priced and a flat tempo term added for the cast itself, floored so that
     * it never outbids a killing blow.
     */
    case "channel": {
      if (!s.heroes[side].castThisRound) return NEVER;
      return racial.manaPerKnowledge * s.heroes[side].knowledge * MANA_VALUE;
    }
    case "raise": {
      const n = raiseCount(s, side, racial.perKill, racial.cap);
      if (n <= 0) return NEVER;
      const cd = content.creatures.get(racial.creature);
      // The same discount a gate takes, and for the same reason: the raised
      // stack dies with the battle and arrives with no modifiers on it.
      return Math.floor(((cd?.aiValue ?? 0) * n * GATE_DISCOUNT_BP) / 10000);
    }
    /**
     * Poison across the enemy line, priced as the hit points it removes rather
     * than as damage: it does not kill anything today, it makes everything
     * cheaper to kill for the rest of the fight.
     */
    case "plague": {
      let value = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] === side) continue;
        const maxHp = st.maxHp[i] ?? 1;
        const bite = Math.max(1, Math.floor((maxHp * racial.hpPctBp) / 10000));
        const aiValue = content.creatures.get(st.def[i]!)?.aiValue ?? 0;
        value += Math.floor((aiValue * bite * (st.count[i] ?? 0)) / (maxHp > 0 ? maxHp : 1));
      }
      return value;
    }
    /**
     * Elemental protection is worth what the OTHER hero can throw, and the
     * exchange table cannot see spell damage at all - so it is priced the way
     * `RULE_VALUE_BP` prices the elemental wards: a token amount, never more
     * than a blow, and nothing at all against a hero with no spellbook.
     */
    case "attune": {
      const foe = s.heroes[1 - side];
      if (foe === undefined || foe.id === null || foe.spellbook.length === 0) return NEVER;
      let living = 0;
      for (let i = 0; i < st.n; i++) if (isAlive(st, i) && st.side[i] === side) living += 1;
      return living * racial.rounds * 12;
    }
    /**
     * Ammunition, priced at the shots it gives back times what a shot is worth -
     * and `Exchange.out` for a shooter IS what its shot is worth.
     */
    case "press": {
      let value = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if ((st.flags[i]! & F_SHOOTER) === 0) continue;
        const missing = st.shots[i]! <= 0 ? racial.shots : 0;
        if (missing <= 0) continue;
        value += (x.out[i] ?? 0) * Math.min(missing, racial.shots);
      }
      return value;
    }
    case "overhaul": {
      let missing = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if ((st.flags[i]! & (F_NON_LIVING | F_WAR_MACHINE)) === 0) continue;
        missing += Math.max(0, (st.maxHp[i] ?? 0) - (st.topHp[i] ?? 0));
      }
      return Math.min(missing, racial.heal) * 3;
    }
    case "runegift": {
      let living = 0;
      for (let i = 0; i < st.n; i++) if (isAlive(st, i) && st.side[i] === side) living += 1;
      return racial.levels * living * 6;
    }
    /**
     * Flooding is worth nothing to an army that does not fight in the water,
     * and everything about which army that is lives in content. The scorer
     * cannot ask "is this Blackwater" (D9), so it asks the question the rule
     * actually turns on: how many of our stacks read the wet mask at all.
     */
    case "flood": {
      let pack = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if (isWetNativeStack(s, i)) pack += 1;
      }
      return pack === 0 ? NEVER : pack * racial.hexes * 4;
    }
  }
}

/**
 * An activated ability, priced at what it does to the board rather than at what
 * it costs. All three are support: none of them destroys anything, so none is
 * lifted, and every one of them loses to a blow that kills - which is right.
 */
function scoreActivate(
  s: BattleState, content: ContentRegistry, x: Exchange, slot: number,
): number {
  const st = s.stacks;
  const side = st.side[slot]!;
  let effect: string | null = null;
  let power = 0;
  for (const a of abilitiesOfSlot(s, content, slot)) {
    if (a.kind === "ACTIVATED") { effect = a.effect; power = a.power; }
  }
  if (effect === null) return NEVER;
  switch (effect) {
    /**
     * Healing, in the currency every heal in this file uses: the share of a
     * creature's own value the mended hit points are worth. It used to be
     * `min(missing, power) * 2`, a number in no currency at all, and a sweep of
     * 78 battles across all thirteen factions chose an activated ability TWICE
     * - the abilities were not badly priced so much as priced in nothing.
     *
     * Capped at what the ability can actually mend and at what is actually
     * wounded, so a full-strength line gains nothing from it.
     */
    case "fortify": {
      let value = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        if (i !== slot && hexDistance(st.hex[i]!, st.hex[slot]!) !== 1) continue;
        const maxHp = st.maxHp[i] ?? 1;
        const mended = Math.min(power, Math.max(0, maxHp - (st.topHp[i] ?? maxHp)));
        const aiValue = content.creatures.get(st.def[i]!)?.aiValue ?? 0;
        value += Math.floor((aiValue * mended) / (maxHp > 0 ? maxHp : 1));
      }
      return value;
    }
    /**
     * Attack across the line, priced against what that line can actually
     * destroy. `ATTACK_STEP_FP` is 50 - five per cent of damage per point - and
     * `Exchange.out` is what each stack's best blow is worth, so the two
     * multiply into "how much more this army destroys", which is the currency
     * everything else here is in.
     */
    case "warcry": {
      let out = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] !== side) continue;
        out += x.out[i] ?? 0;
      }
      return Math.floor((out * power * 50 * WARCRY_ROUNDS) / 1000);
    }
    case "venom": {
      let touched = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] === side) continue;
        if (hexDistance(st.hex[i]!, st.hex[slot]!) === 1) touched += 1;
      }
      return touched * power * 4;
    }
    /**
     * Morale off the stacks it is standing among. Worth the tempo a lost turn
     * costs them, and worth NOTHING against a line already at the floor or made
     * of things that do not roll morale - which is most of what a Gravewatch
     * army meets, and the reason this is counted rather than assumed.
     */
    case "terrify": {
      let touched = 0;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i) || st.side[i] === side) continue;
        if ((st.morale[i] ?? 0) <= -3) continue;
        if (hexDistance(st.hex[i]!, st.hex[slot]!) !== 1) continue;
        touched += 1;
      }
      return touched * power * 10;
    }
    default: return 0;
  }
}

// ------------------------------------------------------------------ choose

/**
 * Score every legal command for `side` and return them best first. The caller
 * takes `[0]`; the list exists so a test - or a designer - can see the second
 * choice and the margin.
 */
/** A banked strike is a deferred one: half of the blow it will become. */
export const DEVOUR_BANK_BP = 5000;

/** The enemy must outweigh us this many times over before running is on the table. */
export const RETREAT_ODDS_BP = 40000;
/** A hero's worth per primary-skill point, for the one decision that trades an army for him. */
export const RETREAT_HERO_POINT_VALUE = 700;

function scoreRetreat(s: BattleState, content: ContentRegistry, side: 0 | 1): number {
  const hero = s.heroes[side];
  if (hero.id === null) return NEVER;
  const st = s.stacks;
  let ours = 0, theirs = 0;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i)) continue;
    const v = (content.creatures.get(st.def[i]!)?.aiValue ?? 0) * (st.count[i] ?? 0);
    if (st.side[i] === side) ours += v; else theirs += v;
  }
  if (theirs * 10000 < ours * RETREAT_ODDS_BP) return NEVER;
  const worth = RETREAT_HERO_POINT_VALUE * (hero.attack + hero.defense + hero.spellPower + hero.knowledge);
  return worth > ours ? worth : NEVER;
}

/** The fan, summed: what it destroys of theirs less what it destroys of ours. */
function scoreHeatStroke(s: BattleState, content: ContentRegistry, slot: number): number {
  const st = s.stacks;
  const side = st.side[slot]!;
  const hexes: number[] = [];
  const k = heatStrokeHexes(st.hex[slot]!, st.facing[slot]!, hexes);
  const seen = new Set<number>();
  let net = 0;
  for (let i = 0; i < k; i++) {
    for (let v = 0; v < st.n; v++) {
      if (v === slot || !isAlive(st, v) || seen.has(v)) continue;
      const own: number[] = [];
      const oc = stackHexes(st, v, own);
      let touched = false;
      for (let m = 0; m < oc; m++) if (own[m] === hexes[i]) { touched = true; break; }
      if (!touched) continue;
      seen.add(v);
      const value = damageValue(s, content, v, expectedDamage(s, content, slot, v, false));
      net += st.side[v] === side ? -value : value;
    }
  }
  return net;
}

export function scoreBattleCommands(
  s: BattleState, side: 0 | 1, content: ContentRegistry, p: DifficultyProfile,
  legal?: readonly BattleCommand[],
): BattleCandidate[] {
  const cmds = legal ?? enumerateLegalBattleCommands(s, side, content);
  const out: BattleCandidate[] = [];
  const lift = (net: number): number => (net > 0 ? net + STRIKE_FLOOR : net);
  const cache = new Map<number, number>();

  let canStrike = false;
  let hasCast = false;
  // Retreat is decided after the casts are priced: a hero with a spell still
  // worth casting fights on, and runs only once the book buys nothing.
  let retreat: BattleCommand | null = null;
  let castOnOffer = false;
  for (const cmd of cmds) {
    if (cmd.type === "BATTLE_ATTACK" || cmd.type === "BATTLE_SHOOT") canStrike = true;
    // The racial needs the exchange table too - two of the thirteen are priced
    // against what a stack's blow is worth.
    if (cmd.type === "BATTLE_CAST" || cmd.type === "BATTLE_CREATURE_CAST"
      || cmd.type === "BATTLE_RACIAL" || cmd.type === "BATTLE_ACTIVATE"
      || cmd.type === "BATTLE_MEDITATE" || cmd.type === "BATTLE_DEVOUR"
      || cmd.type === "BATTLE_HEAT_STROKE") hasCast = true;
  }
  // The exchange table is an n-squared sweep of the damage formula, so it is
  // built only when a spell is actually on offer - which is most plies of a
  // battle between two heroes and no ply at all of a fight against a guard.
  const exchange = hasCast ? measureExchange(s, content) : null;

  for (const cmd of cmds) {
    switch (cmd.type) {
      case "BATTLE_SHOOT":
        out.push({ cmd, score: lift(scoreShoot(s, content, cmd.stack, cmd.target)), why: "shoot" });
        break;
      case "BATTLE_ATTACK":
        out.push({
          cmd,
          score: lift(scoreAttack(
            s, content, p, cmd.stack, cmd.hex, cmd.target, cache, cmd.magic === true,
          )),
          why: cmd.magic === true ? "charged attack" : "attack",
        });
        break;
      case "BATTLE_MOVE":
        out.push({ cmd, score: scoreMove(s, content, p, cmd.stack, cmd.hex, cache), why: "move" });
        break;
      case "BATTLE_CATAPULT":
        out.push({ cmd, score: 50, why: "siege" });
        break;
      case "BATTLE_DEFEND":
        out.push({ cmd, score: 0, why: "defend" });
        break;
      case "BATTLE_WAIT":
        // Waiting is only ever better than defending when there is nothing to
        // hit yet and the enemy has to come to us.
        out.push({ cmd, score: canStrike ? -1 : 1, why: "wait" });
        break;
      case "BATTLE_TACTICS_MOVE":
        out.push({
          cmd, score: scoreTactics(s, content, side, cmd.stack, cmd.hex), why: "place",
        });
        break;
      case "BATTLE_TACTICS_DONE":
        out.push({ cmd, score: 0, why: "placed" });
        break;
      // A cast that does not come out ahead is not scored against moving: a
      // Fireball down one's own line has a *negative* value, and letting that
      // compete with position would let it win a turn where every square is
      // worse. Only a cast worth making is offered at all.
      case "BATTLE_CAST": {
        const raw = exchange === null
          ? NEVER : scoreCast(s, content, exchange, cmd.side, cmd.spell, cmd.target);
        if (raw > 0) castOnOffer = true;
        out.push({ cmd, score: raw > 0 ? lift(raw) : NEVER, why: "cast" });
        break;
      }
      case "BATTLE_CREATURE_CAST": {
        const raw = exchange === null
          ? 0 : scoreCreatureCast(s, content, exchange, cmd.stack, cmd.spell, cmd.target);
        out.push({ cmd, score: raw > 0 ? lift(raw) : NEVER, why: "cast.creature" });
        break;
      }
      case "BATTLE_HEAL":
        out.push({ cmd, score: 1, why: "heal" });
        break;
      /**
       * The hero's blow, in the same currency as everything else: what it
       * destroys. It competes with the casts on equal terms and wins the round
       * whenever the book has nothing better, which is exactly the case the
       * command was added for.
       */
      case "BATTLE_HERO_STRIKE": {
        const dealt = heroStrikeDamage(s, cmd.side, cmd.target);
        const v = damageValue(s, content, cmd.target, dealt);
        /**
         * Scored gross, exactly as a cast is, and NOT net of the turn it costs.
         *
         * That looks wrong and was measured both ways. The blow ends the active
         * stack's turn, so the obvious pricing is to subtract what that stack
         * could have destroyed instead (`Exchange.out`). Measured over 36
         * AI-vs-AI battles, three faction pairs, twelve seeds each, same armies
         * and same seeds in both arms:
         *
         *   gross   36 of 36 decided,  7452 stack-turns, 1006 strikes
         *   net     31 of 36 decided,  9949 stack-turns,  556 strikes
         *
         * Netting made the AI hoard the blow and shuffle instead: `out` is the
         * best blow a stack could land *if it could reach*, so an approaching
         * stack's opportunity cost reads as enormous and the hero never helps.
         * The gross figure is also what `BATTLE_CAST` uses, so the two hero
         * actions are priced the same way, which is the property that matters
         * most - they are alternatives for one slot in the round.
         */
        /**
         * The one thing that IS netted: what the hero takes for it.
         *
         * A counterblow is not an opportunity cost - it is damage the kingdom
         * actually suffers, and past `heroStamina` it ends the battle as a
         * retreat, which costs the whole army. So it is charged, and charged
         * steeply as the hero runs out of room: the last blow a hero can afford
         * is worth `HERO_RISK_FLOOR` of the fight, and one he cannot afford is
         * refused outright.
         *
         * Nothing is charged when the strike KILLS the stack, because a corpse
         * does not answer - which the scorer gets right for free, since the
         * counter is only asked for when the target survives.
         */
        const counter = heroCounterCost(s, content, cmd.side, cmd.target, dealt);
        if (counter === null) {
          out.push({ cmd, score: NEVER, why: "hero.strike.suicide" });
          break;
        }
        const net = v - counter;
        out.push({ cmd, score: net > 0 ? lift(net) : NEVER, why: "hero.strike" });
        break;
      }
      /**
       * The racial. One charge for the battle, so the score has to be the value
       * of using it *now* rather than the value of the effect - which is why
       * each arm is priced against what the same turn could have bought and
       * none of them is lifted above a strike that kills something.
       */
      case "BATTLE_RACIAL":
        out.push({
          cmd,
          score: exchange === null
            ? NEVER : scoreRacial(s, content, exchange, cmd.side, cmd.stack),
          why: "racial",
        });
        break;
      case "BATTLE_ACTIVATE":
        out.push({
          cmd,
          score: exchange === null
            ? NEVER : scoreActivate(s, content, exchange, cmd.stack),
          why: "activate",
        });
        break;
      /**
       * Three verbs the kernel enumerated and this switch never named, so the
       * AI held a Sun Serpent, a Steam Colossus and an Dune Devourers and used none of
       * them. Each is priced in the currency of everything else here - value
       * destroyed or value kept - and none is lifted above a killing blow.
       *
       * Meditation is worth what the enemy would otherwise take off this stack
       * this round: a stack nobody can reach gains nothing by sitting down.
       * Devouring banks a strike, worth about half of this stack's best blow -
       * half, because the blow is deferred and the corpse may be somebody
       * else's dinner. Heat stroke is the fan summed, friend and foe: a vent
       * that cooks more of our own line than theirs comes out negative and is
       * never offered.
       */
      case "BATTLE_MEDITATE": {
        const kept = exchange === null ? 0 : exchange.inn[cmd.stack] ?? 0;
        out.push({ cmd, score: kept > 0 ? kept : NEVER, why: "meditate" });
        break;
      }
      case "BATTLE_DEVOUR": {
        const blow = exchange === null ? 0 : exchange.out[cmd.stack] ?? 0;
        const banked = Math.floor((blow * DEVOUR_BANK_BP) / 10000);
        out.push({ cmd, score: banked > 0 ? banked : NEVER, why: "devour" });
        break;
      }
      case "BATTLE_HEAT_STROKE": {
        const net = scoreHeatStroke(s, content, cmd.stack);
        out.push({ cmd, score: net > 0 ? lift(net) : NEVER, why: "heat_stroke" });
        break;
      }
      /**
       * Running away, when the fight is lost and the hero is worth more than
       * what is left of his army. Retreat costs the army and keeps the hero
       * in the Tavern; losing costs both. So it is worth exactly the hero,
       * and only when the odds say the army is going anyway. Surrender is
       * still never chosen: it costs gold on top, and the kernel's re-hire
       * hands the army back only to the surrendered - a bargain the scorer
       * cannot yet price against the Tavern's own gold.
       */
      case "BATTLE_RETREAT":
        retreat = cmd;
        break;
      case "BATTLE_SURRENDER":
        out.push({ cmd, score: NEVER, why: "quit" });
        break;
    }
  }

  if (retreat !== null && retreat.type === "BATTLE_RETREAT") {
    // Lifted like a blow, so a hero worth more than his army outranks the
    // one strike he could still land before losing him too.
    const raw = castOnOffer ? NEVER : scoreRetreat(s, content, retreat.side);
    out.push({ cmd: retreat, score: raw > 0 ? lift(raw) : NEVER, why: "retreat" });
  }

  // Deterministic: score descending, then the command's own stable key. A stack
  // index tie must never resolve on enumeration accident, or two runs of the
  // same battle diverge.
  out.sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    const ka = battleCommandKey(a.cmd), kb = battleCommandKey(b.cmd);
    return ka === kb ? 0 : ka < kb ? -1 : 1;
  });
  return out;
}

export function chooseBattleCommand(
  s: BattleState, side: 0 | 1, content: ContentRegistry, p: DifficultyProfile,
  legal?: readonly BattleCommand[],
): BattleCommand | null {
  const scored = scoreBattleCommands(s, side, content, p, legal);
  return scored.length === 0 ? null : scored[0]!.cmd;
}

/** Is the given stack in melee contact, i.e. would a shot be refused? */
export const inMelee = (s: BattleState, slot: number): boolean => meleeBlocked(s, slot);
